import { useState, useEffect, forwardRef } from 'react';
import _isEmpty from 'lodash/isEmpty';

import { Modal, ModalMethods, Button, Text, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';

type CategoryType = {
  id: number;
  name: string;
  boutique_id: number;
};

type EditCategoryNameModalProps = {
  category: CategoryType | null;
  onSaved?: (updatedName: string) => void;
};

const EditCategoryNameModal = forwardRef<ModalMethods, EditCategoryNameModalProps>(
  ({ category, onSaved }, ref) => {
    const [name, setName] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (category) {
        setName(category.name);
      } else {
        setName('');
      }
    }, [category]);

    const handleSave = async () => {
      const trimmedName = name.trim();

      if (_isEmpty(trimmedName)) {
        toasts('error', 'Please enter a category name', 'category-name-required');

        return;
      }

      if (!category) {
        toasts('error', 'Invalid category data', 'edit-category-invalid');

        return;
      }

      setIsSaving(true);

      try {
        const encodedName = encodeURIComponent(trimmedName);
        const response = await api.putRequest(
          `custom_outfit/category/update/${category.id}?name=${encodedName}`,
          {}
        );

        if (response.status) {
          toasts('success', 'Category name updated successfully', 'category-name-update-success');

          if (onSaved) {
            onSaved(trimmedName);
          }

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts(
            'error',
            response.message || 'Failed to update category name',
            'category-name-update-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'category-name-update-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Modal ref={ref} size="xl" title="Edit category name" showFooter={false} showCloseIcon>
        <div className="flex flex-col gap-4">
          {category && (
            <Text className="font-inter text-xs text-gray-500" color="black">
              Selected: {category.name}
            </Text>
          )}
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Enter category name"
            className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          />
          <Button
            label="Save Changes"
            size="medium"
            bgColor="primary"
            onClick={handleSave}
            disabled={isSaving}
          />
        </div>
      </Modal>
    );
  }
);

EditCategoryNameModal.displayName = 'EditCategoryNameModal';

export default EditCategoryNameModal;
